import Loader from "components/design/loader";
import { Chip } from "components/DesignSystem/Chips/Chips";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { defaultSortOption, sortOptions } from "constants/foreignCATask";
import {
  NO_SERVICE_TEAM_NAME,
  NO_SERVICE_TEAM_UUID,
} from "constants/settingServiceProvider";
import { COMPANY_GROUP_TAG } from "constants/tagCategory";
import { NO_TAG, NO_TAG_KEY } from "constants/tagColors";
import {
  ACCOUNTANT_ACCESS_OPTIONS,
  BANK_CONNECTION_OPTIONS,
  SERVICE_FILTER_OPTIONS,
} from "constants/task";
import { useUpdateQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import { useGetTagsForCategoryQuery } from "store/apis/billing";
import { useGetAllInkleCPATeamsQuery } from "store/apis/internalTeamSetting";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { useGetServiceTeamQuery } from "store/apis/serviceTeam";
import { Tag } from "types/Models/FCA";

type BookkeepingTrackerFilterType = {
  selectedTag: string | null;
  selectedCompanyTag: string | null;
  tags: Tag[];
  selectedServicesFilter: string | null;
  taskStates: { name: string; uuid: string }[];
  selectedSeason: string | null;
  selectedSort: string | null;
  showSortFeature: boolean;
  selectedTeams: string | null;
  selectedMembers: string | null;
  serviceTeamId?: string;
  showUnassignedTeam: string | null;
  showUnassignedBookkeeper: string | null;
  selectedAccountAccess: string | null;
  selectedBankConnection: string | null;
  selectedFormationType: string | null;
};

export const BookkeepingTrackerFilter = ({
  selectedTag,
  selectedCompanyTag,
  tags,
  selectedServicesFilter,
  taskStates,
  selectedSeason,
  selectedSort,
  selectedTeams,
  selectedMembers,
  serviceTeamId,
  showUnassignedTeam,
  showUnassignedBookkeeper,
  selectedBankConnection,
  selectedAccountAccess,
  selectedFormationType,
}: BookkeepingTrackerFilterType) => {
  const { update } = useUpdateQuery();
  const [hideSortDiv, setHideSortDiv] = useState(true);
  const { isAdmin } = useRoleBasedView();
  const { data: companyTagsData = [] } = useGetTagsForCategoryQuery({
    tagType: COMPANY_GROUP_TAG,
  });
  const { data: teamOptions = [] } = useGetAllInkleCPATeamsQuery(
    {
      accessibleTeams: "CPA_TEAM",
    },
    { skip: !isAdmin }
  );
  const serviceTeamOptions = [
    ...teamOptions,
    { uuid: NO_SERVICE_TEAM_UUID, name: NO_SERVICE_TEAM_NAME },
  ];
  const { data: teamMembers = [] } = useGetServiceTeamQuery(
    {
      serviceTeamId: selectedTeams || serviceTeamId,
    },
    { skip: !selectedTeams && !serviceTeamId, refetchOnMountOrArgChange: true }
  );
  const companyTags = [...companyTagsData, { title: NO_TAG, uuid: NO_TAG_KEY }];

  const { isLoading: isCountiesLoading, data } = useGetCountriesDataQuery();
  const US_FORMATION_TYPE =
    data?.find(
      ({ name, code_alpha_2 }) =>
        code_alpha_2 === "US" && name === "United States"
    )?.formation_type || [];

  const seasonFilter = (values: string[] | null) => {
    update({ query: "season_filter_uuids", value: values?.join(",") || null });
  };

  const serviceTeamFilter = (values: string[] | null) => {
    update({ query: "service_team_uuids", value: values?.join(",") || null });
  };

  const teamMemberFilter = (values: string[] | null) => {
    update({ query: "team_member_uuids", value: values?.join(",") || null });
  };

  const updateTagFilter = (values: string[] | null) => {
    update({
      query: "task_tag_filter_uuids",
      value: values?.join(",") || null,
    });
  };

  const updateCompanyTagFilter = (values: string[] | null) => {
    update({
      query: "company_tag_filter_uuids",
      value: values?.join(",") || null,
    });
  };

  const servicesFilter = (values: string[] | null) => {
    update({
      query: "service_task_filter_uuids",
      value: values?.join(",") || null,
    });
  };

  const updateBankConnectionFilter = (value: string | null) => {
    update({ query: "bank_connections", value: value || null });
  };

  const updateAccountantAccessFilter = (value: string | null) => {
    update({ query: "accountant_access", value: value || null });
  };

  const updateNoTeamFilter = () => {
    update({
      query: "show_unassigned_team",
      value: showUnassignedTeam ? null : true,
    });
  };

  const updateNoAssigneeFilter = () => {
    update({
      query: "show_unassigned_bookkeeper",
      value: showUnassignedBookkeeper ? null : true,
    });
  };

  const updateFormationTypeFilter = (values: string[] | null) => {
    update({
      query: "formation_type_filter_uuids",
      value: values?.join(",") || null,
    });
  };

  const selectedSortOption = sortOptions.find(
    (option) => option.sortKey === selectedSort
  );

  return (
    <div className="t-flex t-items-baseline t-justify-between t-gap-12">
      <div className="t-flex t-items-center t-gap-2">
        <Filter.Root
          defaultValue="SERVICES"
          title="Filter"
          capsule={
            <>
              {isAdmin && (
                <Chip
                  onClose={updateNoTeamFilter}
                  isActive={Boolean(showUnassignedTeam)}
                  isFixedFilter
                  onClick={updateNoTeamFilter}
                >
                  Unassigned Team
                </Chip>
              )}
              {isAdmin && (
                <Chip
                  onClose={updateNoAssigneeFilter}
                  isActive={Boolean(showUnassignedBookkeeper)}
                  isFixedFilter
                  onClick={updateNoAssigneeFilter}
                >
                  Unassigned Bookkeeper
                </Chip>
              )}
              {selectedFormationType &&
                selectedFormationType.split(",").length > 0 && (
                  <Chip
                    onClose={() => updateFormationTypeFilter(null)}
                    filterType="FORMATION_TYPE"
                    isActive
                  >
                    Formation Type ({selectedFormationType.split(",").length})
                  </Chip>
                )}
              {selectedServicesFilter &&
                selectedServicesFilter?.split(",").length > 0 && (
                  <Chip
                    onClose={() => servicesFilter(null)}
                    isActive
                    filterType="SERVICES"
                  >
                    Services ({selectedServicesFilter.split(",").length})
                  </Chip>
                )}
              {selectedSeason && selectedSeason?.split(",").length > 0 && (
                <Chip
                  onClose={() => seasonFilter(null)}
                  isActive
                  filterType="SEASON"
                >
                  Season ({selectedSeason.split(",").length})
                </Chip>
              )}
              {selectedTag && selectedTag?.split(",").length > 0 && (
                <Chip
                  onClose={() => updateTagFilter(null)}
                  isActive
                  filterType="TAG"
                >
                  Task Tag ({selectedTag.split(",").length})
                </Chip>
              )}
              {selectedCompanyTag &&
                selectedCompanyTag?.split(",").length > 0 && (
                  <Chip
                    onClose={() => updateCompanyTagFilter(null)}
                    isActive
                    filterType="COMPANY_TAG"
                  >
                    Company Tag ({selectedCompanyTag.split(",").length})
                  </Chip>
                )}
              {selectedTeams && selectedTeams?.split(",").length > 0 && (
                <Chip
                  onClose={() => serviceTeamFilter([])}
                  isActive
                  filterType="SERVICE_TEAM"
                >
                  Service Team ({selectedTeams?.split(",").length})
                </Chip>
              )}
              {selectedMembers && selectedMembers?.split(",").length > 0 && (
                <Chip
                  onClose={() => teamMemberFilter([])}
                  isActive
                  filterType="SERVICE_TEAM_MEMBER"
                >
                  Bookkeeper ({selectedMembers?.split(",").length})
                </Chip>
              )}
              {selectedBankConnection && (
                <Chip
                  onClose={() => updateBankConnectionFilter(null)}
                  isActive
                  filterType="BANK_CONNECTION"
                >
                  Bank Connection
                </Chip>
              )}

              {selectedAccountAccess && (
                <Chip
                  onClose={() => updateAccountantAccessFilter(null)}
                  isActive
                  filterType="ACCOUNTANT_ACCESS"
                >
                  Accountant access
                </Chip>
              )}
            </>
          }
        >
          <Filter.Portal>
            <Filter.List>
              <Filter.ListItem value="SERVICES">Services</Filter.ListItem>
              <Filter.ListItem value="SEASON">Season</Filter.ListItem>
              <Filter.ListItem value="TAG">Tag</Filter.ListItem>
              <Filter.ListItem value="COMPANY_TAG">Company Tag</Filter.ListItem>
              <Filter.ListItem value="SERVICE_TEAM">
                Service Team
              </Filter.ListItem>
              {(selectedTeams || serviceTeamId) && (
                <Filter.ListItem value="SERVICE_TEAM_MEMBER">
                  Bookkkeeper
                </Filter.ListItem>
              )}
              <Filter.ListItem value="FORMATION_TYPE">
                Formation Type
              </Filter.ListItem>
              <Filter.ListItem value="BANK_CONNECTION">
                Bank connections
              </Filter.ListItem>
              <Filter.ListItem value="ACCOUNTANT_ACCESS">
                Accountant access
              </Filter.ListItem>
            </Filter.List>
            <Filter.Body value="SERVICES" block>
              {taskStates && (
                <MultiSelectFilter
                  onChange={servicesFilter}
                  options={SERVICE_FILTER_OPTIONS}
                  selected={selectedServicesFilter?.split(",")}
                />
              )}
            </Filter.Body>
            <Filter.Body value="SEASON">
              <MultiSelectFilter
                onChange={seasonFilter}
                options={[
                  {
                    label: "2024",
                    value: "2024",
                  },
                  {
                    label: "2025",
                    value: "2025",
                  },
                ]}
                selected={selectedSeason?.split(",")}
              />
            </Filter.Body>
            <Filter.Body value="TAG" block>
              {tags && (
                <MultiSelectFilter
                  onChange={updateTagFilter}
                  options={tags.map((tag) => ({
                    value: tag.uuid,
                    label: tag.title,
                  }))}
                  selected={selectedTag?.split(",")}
                />
              )}
            </Filter.Body>
            <Filter.Body value="COMPANY_TAG" block>
              {companyTags && (
                <MultiSelectFilter
                  onChange={updateCompanyTagFilter}
                  options={companyTags.map((tag) => ({
                    value: tag.uuid,
                    label: tag.title,
                  }))}
                  selected={selectedCompanyTag?.split(",")}
                />
              )}
            </Filter.Body>
            <Filter.Body value="SERVICE_TEAM" block>
              <MultiSelectFilter
                onChange={serviceTeamFilter}
                options={serviceTeamOptions?.map((team) => ({
                  value: team.uuid,
                  label: team.name,
                }))}
                selected={selectedTeams?.split(",")}
              />
            </Filter.Body>
            <Filter.Body value="SERVICE_TEAM_MEMBER" block>
              <MultiSelectFilter
                onChange={teamMemberFilter}
                options={teamMembers?.map((member) => ({
                  value: member.uuid,
                  label: member.name,
                }))}
                selected={selectedMembers?.split(",")}
              />
            </Filter.Body>
            <Filter.Body value="FORMATION_TYPE" block>
              {isCountiesLoading ? (
                <Loader size="small" />
              ) : (
                <MultiSelectFilter
                  isSearchable={false}
                  onChange={updateFormationTypeFilter}
                  options={US_FORMATION_TYPE.map(({ name, uuid }) => ({
                    value: uuid,
                    label: name,
                  }))}
                  selected={selectedFormationType?.split(",") || []}
                />
              )}
            </Filter.Body>
            <Filter.Body value="BANK_CONNECTION" block>
              <Radio.Root onValueChange={updateBankConnectionFilter}>
                <Radio.Content>
                  {BANK_CONNECTION_OPTIONS?.map((bank) => (
                    <Radio.Item
                      asChild
                      value={bank.value}
                      key={bank.value}
                      checked={selectedBankConnection === bank.value}
                    >
                      {bank.label}
                    </Radio.Item>
                  ))}
                </Radio.Content>
              </Radio.Root>
            </Filter.Body>
            <Filter.Body value="ACCOUNTANT_ACCESS" block>
              <Radio.Root onValueChange={updateAccountantAccessFilter}>
                <Radio.Content>
                  {ACCOUNTANT_ACCESS_OPTIONS?.map((access) => (
                    <Radio.Item
                      asChild
                      value={access.value}
                      key={access.value}
                      checked={selectedAccountAccess === access.value}
                    >
                      {access.label}
                    </Radio.Item>
                  ))}
                </Radio.Content>
              </Radio.Root>
            </Filter.Body>
          </Filter.Portal>
        </Filter.Root>
      </div>

      <div className="t-min-w-60">
        <Combobox
          fullWidth
          size="small"
          menuPlacement="auto"
          creatable={false}
          withForm={false}
          components={{
            ClearIndicator: () => null,
          }}
          onChange={(prop: SingleValue<OptionData> | MultiValue<OptionData>) =>
            update({
              query: "task_sort_by",
              value: (prop as SingleValue<OptionData>)?.value || null,
            })
          }
          options={sortOptions.map(({ id, title, sortKey }) => ({
            label: title,
            value: sortKey,
          }))}
          defaultValue={
            selectedSortOption
              ? {
                  label: selectedSortOption.title,
                  value: selectedSortOption.sortKey,
                }
              : {
                  label: defaultSortOption.title,
                  value: "",
                }
          }
          menuPortalTarget={document.body}
        />
      </div>
    </div>
  );
};
